'use client';

import { getClientProvider } from './provider';

import type {
  EvaluationContext,
  FeatureFlagKey,
} from '@/feature-flags/types';
import type { VariantFlagEvaluationResult } from '@/feature-flags/types/variant.types';
import { logEvaluation, logError } from '@/feature-flags/utils/logger';

interface FliptFlagMetadata {
  attachment: string;
  match: boolean;
}

function isFliptFlagMetadata(metadata: unknown): metadata is FliptFlagMetadata {
  return (
    typeof metadata === 'object'
    && metadata !== null
    && 'match' in metadata
    && 'attachment' in metadata
    && typeof (metadata as FliptFlagMetadata).match === 'boolean'
    && typeof (metadata as FliptFlagMetadata).attachment === 'string'
  );
}

export async function evaluateVariantFlagClient<T = unknown>(
  flagKey: FeatureFlagKey,
  userId?: string,
): Promise<VariantFlagEvaluationResult<T>> {
  const startTime = Date.now();

  try {
    const provider = getClientProvider();

    if (!provider) {
      return createVariantFallbackResult<T>(flagKey);
    }

    const context: EvaluationContext = userId
      ? { targetingKey: userId }
      : {};

    const logger = {
      error: (...args: unknown[]) => console.error('[OpenFeature]', ...args),
      warn: (...args: unknown[]) => console.warn('[OpenFeature]', ...args),
      info: (...args: unknown[]) => console.info('[OpenFeature]', ...args),
      debug: (...args: unknown[]) => console.debug('[OpenFeature]', ...args),
    };

    const resolutionDetails = await provider.resolveStringEvaluation(
      flagKey,
      'default',
      context,
      logger,
    );

    const variant = resolutionDetails.value;
    let match: boolean;
    let attachment: string | null = null;

    if (resolutionDetails.flagMetadata && isFliptFlagMetadata(resolutionDetails.flagMetadata)) {
      const { match: fliptMatch, attachment: fliptAttachment } = resolutionDetails.flagMetadata;
      match = fliptMatch;
      attachment = fliptAttachment;
    } else {
      match = resolutionDetails.reason === 'TARGETING_MATCH'
        || resolutionDetails.reason === 'MATCH_EVALUATION_REASON';
    }

    const durationMs = Date.now() - startTime;

    const result: VariantFlagEvaluationResult<T> = {
      key: flagKey,
      variant,
      attachment: attachment as T | null,
      match,
      isFallback: false,
      timestamp: Date.now(),
      reason: 'client_variant_evaluation',
      evaluationTimeMs: durationMs,
    };

    logEvaluation(flagKey, result, {
      userId,
      durationMs,
      variant,
    });

    return result;
  } catch (error) {
    logError('Client-side variant flag evaluation failed, using fallback', error, {
      flagKey,
      userId,
      durationMs: Date.now() - startTime,
    });

    return createVariantFallbackResult<T>(flagKey);
  }
}

function createVariantFallbackResult<T>(
  flagKey: FeatureFlagKey,
): VariantFlagEvaluationResult<T> {
  const result: VariantFlagEvaluationResult<T> = {
    key: flagKey,
    variant: null,
    attachment: null,
    match: false,
    isFallback: true,
    timestamp: Date.now(),
    reason: 'client_variant_fallback',
  };

  logEvaluation(flagKey, result);

  return result;
}
